import { type Etapa, formatearRevision } from "@gestiondoc/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { type FormEvent, useEffect, useState } from "react";
import { queryClient, useTRPC } from "../api.ts";
import type { RevisionDetalle } from "../tipos.ts";
import { mensajeDe } from "../utiles.ts";
import { consecuenciaDe, textosDeFirma } from "./firmas.ts";
import { Aviso, Boton, Campo, CLASE_ENTRADA } from "./ui.tsx";

/**
 * Modal 03b · "Firmar y aprobar la Rev. 04". La firma es la imagen que Soporte Tecnológico cargó
 * para la persona (o el trazo que dibujó); se confirma con su contraseña. La API decide qué etapa
 * se firma según el estado de la revisión; acá solo cambian los textos, que salen de la ruta de
 * la revisión (con o sin revisor).
 */
export function ModalFirma({
  revision,
  hashContenido,
  vigente,
  etapa,
  onCerrar,
  onFirmado,
}: {
  revision: RevisionDetalle;
  /** El hash del archivo que la persona tiene en pantalla: si cambió, la API no firma. */
  hashContenido: string;
  /** La revisión vigente hoy, para decir que pasa a obsoleta al aprobar. */
  vigente: RevisionDetalle | null;
  etapa: Etapa;
  onCerrar: () => void;
  onFirmado: (r: { estado: string; idFirma: string }) => void;
}) {
  const trpc = useTRPC();
  const mia = useQuery(trpc.firmas.mia.queryOptions());
  const [contrasena, setContrasena] = useState("");
  const [error, setError] = useState<string | null>(null);
  const rev = formatearRevision(revision.numero);
  const textos = textosDeFirma(etapa, revision);
  const hayVistaPrevia = !!revision.pdfVista || revision.redactada;

  const firmar = useMutation({
    ...trpc.documentos.firmar.mutationOptions({
      onSuccess: (r) => {
        queryClient.invalidateQueries();
        onFirmado(r);
      },
      onError: (e) => setError(mensajeDe(e)),
    }),
    // La contraseña viaja en las variables: que no quede en el caché de mutaciones.
    gcTime: 0,
  });

  function enviar(e: FormEvent) {
    e.preventDefault();
    if (firmar.isPending) return;
    setError(null);
    if (!contrasena) {
      setError("Confirma con tu contraseña");
      return;
    }
    firmar.mutate(
      { revisionId: revision.id, contrasena, hashContenido, etapa },
      { onSettled: () => setContrasena("") },
    );
  }

  const sinImagen = mia.isSuccess && mia.data === null;

  return (
    <Modal
      titulo={`${textos.titulo} la Rev. ${rev}`}
      onCerrar={onCerrar}
      bloqueado={firmar.isPending}
    >
      <form onSubmit={enviar} className="space-y-4">
        <Aviso tono="ambar">{consecuenciaDe(etapa, revision, vigente)}</Aviso>
        {!hayVistaPrevia && etapa !== "elaboro" && (
          <Aviso tono="ambar">
            La vista previa todavía no está lista. Descarga el archivo y revísalo antes de firmar.
          </Aviso>
        )}

        <div>
          <div className="mb-1 text-[11.5px] font-semibold text-texto-2">Tu firma</div>
          <div className="grid h-[150px] place-items-center rounded-md border border-borde bg-fondo">
            {mia.isPending && <span className="text-[12.5px] text-texto-3">Cargando…</span>}
            {mia.data && (
              <img
                src={`/archivos/${mia.data.imagenId}`}
                alt="Tu firma"
                className="max-h-[130px] max-w-[90%] object-contain"
              />
            )}
            {sinImagen && (
              <span className="px-6 text-center text-[12.5px] text-texto-3">
                Todavía no tienes una imagen de firma. Pídele a Soporte Tecnológico que la cargue.
              </span>
            )}
          </div>
          <div className="mt-1 text-[11px] text-texto-3">
            Esta imagen se estampa en el sello junto con tu nombre, la fecha y el ID de firma.
          </div>
        </div>

        <Campo etiqueta="Confirma con tu contraseña">
          <input
            type="password"
            autoComplete="new-password"
            value={contrasena}
            onChange={(e) => setContrasena(e.target.value)}
            className={CLASE_ENTRADA}
          />
        </Campo>

        {error && <Aviso>{error}</Aviso>}

        <div className="flex justify-end gap-2 pt-1">
          <Boton variante="secundario" onClick={onCerrar} disabled={firmar.isPending}>
            Cancelar
          </Boton>
          <Boton
            variante="firma"
            type="submit"
            disabled={firmar.isPending || sinImagen || mia.isPending}
          >
            {firmar.isPending ? "Firmando…" : textos.boton}
          </Boton>
        </div>
      </form>
    </Modal>
  );
}

/**
 * "Devolver con observaciones": vuelve a borrador. Fase 4: si hay marcas abiertas en el texto,
 * las observaciones aparte son opcionales (las marcas ya dicen qué corregir).
 */
export function ModalDevolver({
  revisionId,
  numero,
  marcasAbiertas = 0,
  onCerrar,
  onDevuelta,
}: {
  revisionId: string;
  numero: number;
  marcasAbiertas?: number;
  onCerrar: () => void;
  onDevuelta: () => void;
}) {
  const trpc = useTRPC();
  const [observaciones, setObservaciones] = useState("");
  const [error, setError] = useState<string | null>(null);
  const devolver = useMutation(
    trpc.documentos.devolver.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries();
        onDevuelta();
      },
      onError: (e) => setError(mensajeDe(e)),
    }),
  );

  function enviar(e: FormEvent) {
    e.preventDefault();
    setError(null);
    devolver.mutate({ revisionId, observaciones });
  }

  return (
    <Modal
      titulo={`Devolver la Rev. ${formatearRevision(numero)}`}
      onCerrar={onCerrar}
      bloqueado={devolver.isPending}
    >
      <form onSubmit={enviar} className="space-y-4">
        <p className="text-[12.5px] text-texto-3">
          La revisión vuelve a borrador para quien la elaboró. Las firmas de esta ronda dejan de
          contar: al corregirla, empieza de nuevo por la firma de elaboró.
        </p>
        {marcasAbiertas > 0 && (
          <p className="rounded-md bg-marino-claro/50 px-3 py-2 text-[12.5px] text-marino">
            Va con {marcasAbiertas} marca{marcasAbiertas === 1 ? "" : "s"} abierta
            {marcasAbiertas === 1 ? "" : "s"} sobre el texto: quien elaboró las ve resaltadas en su
            documento.
          </p>
        )}
        <Campo
          etiqueta={marcasAbiertas > 0 ? "Observaciones (opcional)" : "Observaciones"}
          ayuda="Qué hay que corregir y dónde."
        >
          <textarea
            value={observaciones}
            onChange={(e) => setObservaciones(e.target.value)}
            rows={5}
            className={CLASE_ENTRADA}
          />
        </Campo>
        {error && <Aviso>{error}</Aviso>}
        <div className="flex justify-end gap-2 pt-1">
          <Boton variante="secundario" onClick={onCerrar} disabled={devolver.isPending}>
            Cancelar
          </Boton>
          <Boton variante="peligro" type="submit" disabled={devolver.isPending}>
            {devolver.isPending
              ? "Devolviendo…"
              : marcasAbiertas > 0
                ? "Devolver con correcciones"
                : "Devolver con observaciones"}
          </Boton>
        </div>
      </form>
    </Modal>
  );
}

export function Modal({
  titulo,
  onCerrar,
  bloqueado = false,
  children,
}: {
  titulo: string;
  onCerrar: () => void;
  /** Mientras se envía, ni Escape ni el fondo cierran: el resultado tiene que verse. */
  bloqueado?: boolean;
  children: React.ReactNode;
}) {
  useEffect(() => {
    const tecla = (e: KeyboardEvent) => e.key === "Escape" && !bloqueado && onCerrar();
    window.addEventListener("keydown", tecla);
    return () => window.removeEventListener("keydown", tecla);
  }, [onCerrar, bloqueado]);

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4">
      <button
        type="button"
        aria-label="Cerrar"
        onClick={() => !bloqueado && onCerrar()}
        className="absolute inset-0 bg-[rgba(10,18,50,.45)]"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        // Si el contenido no cabe (la ficha de una persona con sus procesos y diligencias), la
        // ventana se desplaza por dentro en vez de quedar cortada arriba y abajo.
        className="relative max-h-full w-full max-w-[520px] overflow-y-auto rounded-lamina bg-white p-6 shadow-[0_30px_60px_rgba(0,0,0,.3)]"
      >
        <h2 className="mb-4 text-[18px] font-bold text-marino">{titulo}</h2>
        {children}
      </div>
    </div>
  );
}
